<template>
  <div class="date-range-picker">
    <Datepicker
      v-model="internalValue"
      :placeholder="placeholder"
      :enable-time-picker="false"
      :format="formatDate"
      range
      :max-date="maxDate instanceof Date ? maxDate : undefined"
      :min-date="minDate instanceof Date ? minDate : undefined"
      auto-apply
      :teleport="true"
      :uid="uid"
      :preview-format="formatDateForDisplay"
      :cancel-text="'Cancel'"
      :submit-text="'Apply'"
      :input-class-name="'v-field__input'"
      :color="primaryColor"
      @update:model-value="handleDateChange"
    >
      <template #trigger>
        <v-text-field
          :label="label"
          :model-value="formatDateRangeForDisplay"
          readonly
          variant="outlined"
          density="compact"
          rounded="lg"
          hide-details
          class="date-input"
          :error="!!error"
          :error-messages="error"
        >
          <template #prepend>
            <v-icon :icon="mdiCalendar"></v-icon>
          </template>
          <template #append>
            <v-btn
              v-if="internalValue && internalValue.length === 2"
              icon
              variant="text"
              density="comfortable"
              @click.stop="clearDates"
            >
              <v-icon :icon="mdiClose"></v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </template>
    </Datepicker>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import { useTheme } from 'vuetify';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { format } from 'date-fns';
import { nanoid } from 'nanoid';
import { mdiCalendar, mdiClose } from '@mdi/js';

interface Props {
  modelValue: [Date | string | null, Date | string | null] | null;
  label?: string;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  error?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  label: 'Date Range',
  placeholder: 'Select date range',
  minDate: undefined,
  maxDate: undefined,
  error: ''
});

const emit = defineEmits(['update:model-value']);

// Generate a unique ID for the datepicker to prevent conflicts
const uid = nanoid();

// Resolve the Vuetify theme primary color for the datepicker accent.
const theme = useTheme();
const primaryColor = computed(() => theme.current.value.colors.primary);

// Helper function to convert value to Date
const toDate = (value: Date | string | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  return null;
};

// Internal state
const internalValue = ref<[Date, Date] | null>(
  props.modelValue && props.modelValue[0] && props.modelValue[1]
    ? [toDate(props.modelValue[0])!, toDate(props.modelValue[1])!]
    : null
);

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  // Avoid unnecessary updates if the values are already the same
  if (!newValue || !newValue[0] || !newValue[1]) {
    if (internalValue.value !== null) {
      internalValue.value = null;
    }
    return;
  }

  // Convert to Date objects
  const startDate = toDate(newValue[0]);
  const endDate = toDate(newValue[1]);
  
  if (!startDate || !endDate) {
    if (internalValue.value !== null) {
      internalValue.value = null;
    }
    return;
  }

  // Check if the current internal value is already equal to the new value
  if (internalValue.value && 
      internalValue.value[0] && 
      internalValue.value[1] &&
      internalValue.value[0].getTime() === startDate.getTime() &&
      internalValue.value[1].getTime() === endDate.getTime()) {
    // Values are already the same, no need to update
    return;
  }

  // Update internal value only if it's actually different
  internalValue.value = [startDate, endDate];
}, { deep: true });

// Format date for the datepicker component
const formatDate = (date: Date) => {
  try {
    // Robust validation to ensure date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    return format(date, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

// Format date for display in the text field
const formatDateForDisplay = (date: Date) => {
  try {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    return format(date, 'MMM dd, yyyy');
  } catch {
    return '';
  }
};

// Format the date range for display in the input field
const formatDateRangeForDisplay = computed(() => {
  if (!internalValue.value || !internalValue.value[0] || !internalValue.value[1]) {
    return '';
  }

  try {
    // Validate both dates before formatting
    const [startDate, endDate] = internalValue.value;
    if (!(startDate instanceof Date) || isNaN(startDate.getTime()) ||
        !(endDate instanceof Date) || isNaN(endDate.getTime())) {
      return '';
    }
    
    const startDateStr = formatDateForDisplay(startDate);
    const endDateStr = formatDateForDisplay(endDate);
    return `${startDateStr} - ${endDateStr}`;
  } catch {
    return '';
  }
});

// Handle date change from the datepicker
const handleDateChange = (value: [Date, Date] | null) => {
  if (!value) {
    internalValue.value = null;
    emit('update:model-value', null);
    return;
  }

  // Ensure both dates are valid before emitting
  if (value[0] instanceof Date && !isNaN(value[0].getTime()) && 
      value[1] instanceof Date && !isNaN(value[1].getTime())) {
    
    // Get today's date for validation
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Ensure neither date is in the future
    const validStart = value[0] > today ? today : new Date(value[0].getTime());
    const validEnd = value[1] > today ? today : new Date(value[1].getTime());
    
    // Create new Date objects to break reference
    const newValue: [Date, Date] = [validStart, validEnd];

    // Update internal value first to prevent watcher conflicts
    internalValue.value = newValue;

    // Emit the validated dates
    emit('update:model-value', newValue);
  }
};

// Clear selected dates
const clearDates = (event: Event) => {
  event.stopPropagation();
  internalValue.value = null;
  emit('update:model-value', null);
};

// Initialize component
onMounted(() => {
  // Ensure initial value is properly set
  if (props.modelValue && props.modelValue[0] && props.modelValue[1]) {
    const start = toDate(props.modelValue[0]);
    const end = toDate(props.modelValue[1]);
    
    // Validate dates to ensure they're not in the future
    if (start && end) {
      // Get today's date for validation
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Check if dates are in the future
      const validStart = start > today ? today : new Date(start.getTime());
      const validEnd = end > today ? today : new Date(end.getTime());
      
      // Set the internal value with validated dates
      internalValue.value = [validStart, validEnd];

      // If we had to modify the dates, emit the corrected values
      if (validStart.getTime() !== start.getTime() || validEnd.getTime() !== end.getTime()) {
        emit('update:model-value', [validStart, validEnd]);
      }
    } else {
      internalValue.value = null;
      emit('update:model-value', null);
    }
  }
});
</script>

<style scoped>
.date-range-picker {
  width: 100%;
}
.date-input {
  width: 100%;
}

/* Override styles for the datepicker to match Vuetify theme */
:deep(.dp__main) {
  font-family: inherit;
}

:deep(.dp__theme_light) {
  --dp-primary-color: rgb(var(--v-theme-primary));
  --dp-border-color: rgba(0, 0, 0, 0.12);
  --dp-border-radius: 4px;
}

:deep(.dp__range_start),
:deep(.dp__range_end),
:deep(.dp__active_date) {
  background-color: var(--dp-primary-color) !important;
  color: white !important;
}

:deep(.dp__range_between) {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
  color: #000 !important;
}
</style>
